import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const rawSvgPattern = /(<svg\b[\s\S]*?<\/svg>)/gi;

const sanitizeSvg = (svg) => {
  const stripped = String(svg || '')
    .replace(/<script\b[\s\S]*?<\/script>/gi, '')
    .replace(/\s+on[a-z]+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, '')
    .replace(/\s+(href|xlink:href)\s*=\s*("|')\s*javascript:[\s\S]*?\2/gi, '');

  if (typeof window === 'undefined' || !window.DOMParser || !window.XMLSerializer) {
    return stripped;
  }

  try {
    const document = new window.DOMParser().parseFromString(stripped, 'image/svg+xml');
    if (document.querySelector('parsererror')) return stripped;

    document.querySelectorAll('script').forEach((node) => node.remove());
    document.querySelectorAll('*').forEach((node) => {
      Array.from(node.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        const value = attribute.value.trim().toLowerCase();
        if (name.startsWith('on') || ((name === 'href' || name === 'xlink:href') && value.startsWith('javascript:'))) {
          node.removeAttribute(attribute.name);
        }
      });
    });

    return new window.XMLSerializer().serializeToString(document.documentElement);
  } catch (error) {
    return stripped;
  }
};

const splitSvgChunks = (content) => {
  const normalized = String(content || '').replace(/```(?:svg|xml)\s*(<svg\b[\s\S]*?<\/svg>)\s*```/gi, '$1');
  const chunks = [];
  let lastIndex = 0;

  normalized.replace(rawSvgPattern, (match, svg, offset) => {
    if (offset > lastIndex) {
      chunks.push({ type: 'markdown', value: normalized.slice(lastIndex, offset) });
    }
    chunks.push({ type: 'svg', value: svg });
    lastIndex = offset + match.length;
    return match;
  });

  if (lastIndex < normalized.length) {
    chunks.push({ type: 'markdown', value: normalized.slice(lastIndex) });
  }

  return chunks.length > 0 ? chunks : [{ type: 'markdown', value: normalized }];
};

const ProblemMarkdown = ({
  children,
  className = '',
  inlineCodeClassName = 'bg-slate-100 text-slate-800 px-1 py-0.5 rounded font-mono',
}) => {
  const chunks = splitSvgChunks(children);

  return (
    <div className={className}>
      {chunks.map((chunk, index) => {
        if (chunk.type === 'svg') {
          return (
            <div
              key={`svg-${index}`}
              className="not-prose my-4 flex justify-center overflow-x-auto rounded-lg"
            >
              <div
                className="problem-svg-renderer max-w-full [&_svg]:h-auto [&_svg]:max-w-full [&_svg_text]:font-sans"
                dangerouslySetInnerHTML={{ __html: sanitizeSvg(chunk.value) }}
              />
            </div>
          );
        }

        if (!chunk.value.trim()) return null;

        return (
          <ReactMarkdown
            key={`markdown-${index}`}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              code({ node, inline, className, children, ...props }) {
                return !inline ? (
                  <code className={className} {...props}>
                    {children}
                  </code>
                ) : (
                  <code className={inlineCodeClassName} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {chunk.value}
          </ReactMarkdown>
        );
      })}
    </div>
  );
};

export default ProblemMarkdown;
